import {
  getDefaultRequiredScheduleForSkill,
  normalizeRequiredSchedule,
} from './requiredSchedule';

const DEFAULT_OUTDOOR_SKILLS = ['ON THE LAWN', 'SOCCER', 'FLAG FOOTBALL', 'ARCHERY', 'BASKETBALL', 'GET MESSY'];

function sanitizeRotationOverride(override) {
  return {
    rotationIndex: Number(override?.rotationIndex) || 0,
    enabled: override?.enabled !== false,
    override: {
      minGroupSize: override?.override?.minGroupSize ?? null,
      maxGroupSize: override?.override?.maxGroupSize ?? null,
    },
  };
}

export function defaultSkillRecord(name, tribeNames = []) {
  return {
    id: null,
    name,
    requiredForEligible: name === 'ONE EIGHT' || name === 'MASU MADNESS',
    repeatable: false,
    allowedGenders: name === 'GIRLS CLUB' ? ['Female'] : name === 'MAN CAVE' ? ['Male'] : ['Male', 'Female'],
    allowedTribes:
      name === 'KAVA ONLY' ? ['Kava']
      : name === 'GET MESSY' ? tribeNames.filter((tribe) => tribe !== 'Kava')
      : name === 'ONE EIGHT' || name === 'MASU MADNESS' ? ['Masu']
      : tribeNames,
    minGroupSize: null,
    maxGroupSize: null,
    fixedRotations: [],
    requiredSchedule: getDefaultRequiredScheduleForSkill(name),
    preferredRotations: [],
    manualOverfillPolicy: 'disallow',
    enableRotationOverrides: false,
    rotationOverrides: [],
  };
}

export function normalizeSkillRecord(skill, tribeNames = []) {
  const { fixedStaff: _fixedStaff, ...restSkill } = skill || {};
  const base = defaultSkillRecord(skill?.name || '', tribeNames);
  const fixedRotations = Array.isArray(skill?.fixedRotations) ? skill.fixedRotations.map(Number).filter(Number.isInteger).filter((n) => n >= 0 && n < 6) : base.fixedRotations;
  const hasStoredRequiredSchedule = skill?.requiredSchedule?.mode === 'fixed'
    || skill?.requiredSchedule?.mode === 'perBlock';
  return {
    ...base,
    ...restSkill,
    name: String(skill?.name || base.name || '').trim(),
    allowedGenders: Array.isArray(skill?.allowedGenders) && skill.allowedGenders.length > 0 ? skill.allowedGenders : base.allowedGenders,
    allowedTribes: Array.isArray(skill?.allowedTribes) && skill.allowedTribes.length > 0 ? skill.allowedTribes : base.allowedTribes,
    fixedRotations,
    requiredSchedule: normalizeRequiredSchedule(
      hasStoredRequiredSchedule ? skill.requiredSchedule : base.requiredSchedule,
      fixedRotations
    ),
    preferredRotations: Array.isArray(skill?.preferredRotations) ? skill.preferredRotations.map(Number).filter(Number.isInteger).filter((n) => n >= 0 && n < 6) : base.preferredRotations,
    manualOverfillPolicy: skill?.manualOverfillPolicy === 'allow'
      ? 'allow'
      : 'disallow',
    rotationOverrides: Array.isArray(skill?.rotationOverrides) ? skill.rotationOverrides.map(sanitizeRotationOverride) : [],
    enableRotationOverrides: Array.isArray(skill?.rotationOverrides) && skill.rotationOverrides.length > 0
      ? true
      : !!skill?.enableRotationOverrides,
  };
}

export function mergeSkillRecords(skillNames, storedSkills = [], tribeNames = []) {
  const mergedNames = new Set(skillNames.filter(Boolean));
  storedSkills.forEach((skill) => mergedNames.add(skill.name));
  return Array.from(mergedNames)
    .sort((a, b) => a.localeCompare(b))
    .map((name) => {
      const existing = storedSkills.find((skill) => skill.name === name);
      return normalizeSkillRecord(existing || defaultSkillRecord(name, tribeNames), tribeNames);
    });
}

export function buildSkillsConfigFromSettings(skillRecords = [], tribeNames = []) {
  const config = {};
  skillRecords.forEach((skill) => {
    if (!skill?.name) return;
    config[skill.name] = {
      allowedGenders: Array.isArray(skill.allowedGenders) && skill.allowedGenders.length > 0 ? skill.allowedGenders : ['Male', 'Female'],
      allowedTribes: Array.isArray(skill.allowedTribes) && skill.allowedTribes.length > 0 ? skill.allowedTribes : tribeNames,
      indoor: !DEFAULT_OUTDOOR_SKILLS.includes(skill.name),
      requiredForEligible: skill.requiredForEligible === true,
      repeatable: skill.repeatable === true,
      minGroupSize: skill.minGroupSize ?? null,
      maxGroupSize: skill.maxGroupSize ?? null,
      fixedRotations: Array.isArray(skill.fixedRotations) ? skill.fixedRotations : [],
      requiredSchedule: normalizeRequiredSchedule(skill.requiredSchedule, skill.fixedRotations),
      preferredRotations: Array.isArray(skill.preferredRotations) ? skill.preferredRotations : [],
      manualOverfillPolicy: skill.manualOverfillPolicy === 'allow' ? 'allow' : 'disallow',
      rotationOverrides: Array.isArray(skill.rotationOverrides) ? skill.rotationOverrides.map(sanitizeRotationOverride) : [],
    };
  });
  return config;
}

export function getRequiredSkillRotations(skillRecords = []) {
  return skillRecords
    .filter((skill) => skill.requiredForEligible && Array.isArray(skill.fixedRotations) && skill.fixedRotations.length > 0)
    .flatMap((skill) => skill.fixedRotations.map((rotationIndex) => ({ skill: skill.name, rotationIndex })));
}

export { sanitizeRotationOverride };
